import { Utils } from 'core/utils';
import { StageUtils } from 'core/utils.stage';

const MIN_DRAG_DISTANCE = 25;

interface Position
{
  clientX: number;
  clientY: number;
  pageX: number;
  pageY: number;
}

abstract class DragDropSVG
{
  protected source: any;
  protected element: HTMLElement;

  private mousedown: boolean = false;
  private dragging: boolean = false;

  private startX: number;
  private startY: number;

  protected abstract dragStartAllowed(event: Event): boolean;
  protected abstract dragStart(event: Event): void;
  protected abstract dragMove(event: Event): void;
  protected abstract dragEnd(event: Event): void;

  constructor(source: any)
  {
    this.source = source;
    this.element = source.element.node;

    // FF is natively able to fire drag events within svgs - wohooo!
    // but for compatibility purposes we disable these built-in drag events :(
    this.element.addEventListener('dragstart', Utils.eventPreventDefault);

    this.element.addEventListener('mousedown', this.startHandler.bind(this));
    this.element.addEventListener('touchstart', this.startHandler.bind(this));

    window.addEventListener('mousemove', this.moveHandler.bind(this));
    window.addEventListener('touchmove', this.moveHandler.bind(this));

    window.addEventListener('mouseup', this.endHandler.bind(this));
    window.addEventListener('touchend', this.endHandler.bind(this));
  }

  public destructor(): void
  {
    this.element.removeEventListener('dragstart', Utils.eventPreventDefault);

    this.element.removeEventListener('mousedown', this.startHandler.bind(this));
    this.element.removeEventListener(
        'touchstart', this.startHandler.bind(this));

    window.removeEventListener('mousemove', this.moveHandler.bind(this));
    window.removeEventListener('touchmove', this.moveHandler.bind(this));

    window.removeEventListener('mouseup', this.endHandler.bind(this));
    window.removeEventListener('touchend', this.endHandler.bind(this));
  }

  private startHandler(event: Event): void
  {
    if (this.dragStartAllowed(event)) {
      const position = this.getPosition(event);
      this.startX = position.clientX;
      this.startY = position.clientY;
      this.mousedown = true;
    }
  }

  private initDragEvent(event: Event): boolean
  {
    const position = this.getPosition(event);
    return (
        !this.dragging &&
        this.mousedown && (
            Math.abs(position.clientX - this.startX) > MIN_DRAG_DISTANCE ||
            Math.abs(position.clientY - this.startY) > MIN_DRAG_DISTANCE)
    );
  }

  private moveHandler(event: Event): void
  {
    if (this.initDragEvent(event)) {
      this.dragStart(event);
      this.dragging = true;

      this.fireEvent(this.element, 'dragStart');
    }
    else if (this.dragging) {
      this.dragMove(event);
    }
  }

  private endHandler(event: Event): void
  {
    this.mousedown = false;
    if (this.dragging) {
      event.preventDefault();
      event.stopPropagation();

      this.dragging = false;

      this.dragEnd(event);
      this.fireEvent(this.element, 'dragEnd');
    }
  }

  protected getPosition(event: Event): Position
  {
    return {
        clientX: (<TouchEvent> event).touches
            ? (<TouchEvent> event).touches[0].clientX
            : (<MouseEvent> event).clientX,
        clientY: (<TouchEvent> event).touches
            ? (<TouchEvent> event).touches[0].clientY
            : (<MouseEvent> event).clientY,
        pageX: (<TouchEvent> event).touches
            ? (<TouchEvent> event).touches[0].pageX
            : (<MouseEvent> event).pageX,
        pageY: (<TouchEvent> event).touches
            ? (<TouchEvent> event).touches[0].pageY
            : (<MouseEvent> event).pageY,
    };
  }

  protected fireEvent(element: HTMLElement, eventType: string): void
  {
    if (element) {
      const dropEvent = document.createEvent('MouseEvents');
      dropEvent.initEvent(eventType, false, true);
      element.dispatchEvent(dropEvent);
    }
  }
}

export class DragDropGhostSVG extends DragDropSVG
{
  private dragOverElement: HTMLElement;
  private ghost: HTMLElement;
  private eventType: string;

  constructor(source: any, eventType: string)
  {
    super(source);
    this.eventType = eventType;
  }

  protected dragStartAllowed(event: Event): boolean
  {
    return !this.source.isUnchanged() && !Utils.isSmallDevice();
  }

  protected dragStart(event: Event): void
  {
    this.ghost = this.createGhostImage(event);
  }

  protected dragMove(event: Event): void
  {
    const position = this.getPosition(event);

    this.ghost.style.left = position.pageX - this.ghost.offsetWidth / 2 + 'px';
    this.ghost.style.top = position.pageY - this.ghost.offsetHeight / 2 + 'px';

    const elementMouseIsOver =
        <HTMLElement> document.elementFromPoint(
            position.clientX, position.clientY);
    if (this.dragOverElement !== elementMouseIsOver) {
      this.fireEvent(this.dragOverElement, 'dragleave');
      this.dragOverElement = elementMouseIsOver;
      this.fireEvent(this.dragOverElement, 'dragenter');
    }
  }

  protected dragEnd(event: Event): void
  {
    StageUtils.removeElement(this.ghost);

    if (this.dragOverElement) {
      const dropEvent = document.createEvent('MouseEvents');
      dropEvent.initEvent(this.eventType, false, true);
      (<any> dropEvent).sourcePlaceholder = this.source;
      this.dragOverElement.dispatchEvent(<MouseEvent> dropEvent);
    }
  }

  private createGhostImage(event: Event): HTMLElement
  {
    const position = this.getPosition(event);

    const zoom = Number(this.source.wrapper.doc().viewbox().zoom);
    const svgClone = this.source.wrapper.node.cloneNode(true);

    let ghost = document.createElement('div');
    ghost.appendChild(svgClone);
    document.body.appendChild(ghost);

    const width = Number(svgClone.width.baseVal.value * zoom);
    const height = Number(svgClone.height.baseVal.value * zoom);

    svgClone.setAttribute('width', width);
    svgClone.setAttribute('height', height);

    ghost.style.height = height + 'px';
    ghost.style.width = width + 'px';
    ghost.style.position = 'absolute';
    ghost.style.left = Number(position.pageX - width / 2) + 'px';
    ghost.style.top = Number(position.pageY - height / 2) + 'px';
    ghost.style.overflow = 'hidden';
    ghost.style.opacity = '0.4';
    ghost.style['pointer-events'] = 'none';

    return ghost;
  }
}

export function applyContentImageSwapping(placeholder: any): DragDropGhostSVG
{
  return new DragDropGhostSVG(placeholder, 'dropSwap');
}



// WEBPACK FOOTER //
// ./src/app/collage-designer/stage/drag-drop.ts